import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { X } from "lucide-react";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { submitQuestion } from "@/lib/academy.functions";

export function AskQuestionModal({
  open,
  onClose,
  contextLabel,
  prefillQuestion,
}: {
  open: boolean;
  onClose: () => void;
  contextLabel?: string;
  prefillQuestion?: string;
}) {
  const submit = useServerFn(submitQuestion);
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState(prefillQuestion ?? "");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const decorated = contextLabel
        ? `[Context: ${contextLabel}]\n\n${question}`
        : question;
      await submit({ data: { email, question: decorated } });
      toast.success("Question submitted. We'll be in touch.");
      setEmail("");
      setQuestion("");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/70 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-cream-50 p-8 shadow-2xl"
        style={{ borderRadius: 6 }}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-charcoal-500 hover:text-navy-900"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          Ask Currency Thoughts
        </p>
        <h3 className="mt-2 font-display text-2xl text-navy-900">
          What's on your mind?
        </h3>
        <p className="mt-2 font-sans text-[14px] text-charcoal-500">
          We answer one family money question every week.
        </p>
        {contextLabel && (
          <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600">
            Re: {contextLabel}
          </p>
        )}
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            maxLength={255}
            className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] text-charcoal-900 outline-none focus:border-bronze-600"
          />
          <textarea
            required
            minLength={10}
            maxLength={2000}
            rows={5}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question..."
            className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] text-charcoal-900 outline-none focus:border-bronze-600"
          />
          <PrimaryButton type="submit" disabled={busy} size="lg" className="w-full">
            {busy ? "Sending..." : "Submit Question"}
          </PrimaryButton>
        </form>
      </div>
    </div>
  );
}

export default AskQuestionModal;